<template>
  <v-card>
    <v-card-title class="body-2 v-card__subtitle">
      <v-icon x-small class="mr-2">mdi-chart-box-outline</v-icon>
      Zusammenfassung

      <v-spacer></v-spacer>
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn depressed small v-bind="attrs" v-on="on" class="my-0 py-0">
            <v-icon left small>mdi-cash-clock</v-icon>
            {{ selectedBatchSelector.text }}
            <v-icon right small>mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-list nav>
          <v-list-item-group v-model="selectedBatchSelector" mandatory>
            <v-list-item
              v-for="batch in payrollBatches"
              :key="batch.meta.id"
              link
              dense
              :value="{
                text: batch.title || formatBatchPeriod(batch),
                value: batch.meta.id,
                batch: batch,
              }"
            >
              <v-list-item-icon>
                <v-icon dense>mdi-calendar-month</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                {{ batch.title || formatBatchPeriod(batch) }}
              </v-list-item-title>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
      <v-btn
        icon
        x-small
        class="ml-1 my-0 py-0"
        @click="refreshData"
        :loading="loading"
      >
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2 text-caption grey--text">Wird geladen...</div>
    </v-card-text>
    <v-card-text v-else-if="!selectedBatch" class="text-center py-4">
      <v-icon large color="grey lighten-1">mdi-calendar-blank</v-icon>
      <div class="mt-2 grey--text">Keine Abrechnungsperiode ausgewählt.</div>
    </v-card-text>
    <v-card-text v-else class="py-2">
      <v-row dense>
        <v-col cols="3">
          <v-card outlined>
            <v-card-text class="text-center py-2 px-0">
              <div class="text-h6 font-weight-bold purple--text">
                <v-icon small class="mr-1" color="purple">mdi-check-all</v-icon
                >{{ statusCounts.toReview }}
              </div>
              <div class="text-caption grey--text hidden-sm-only">
                Zu prüfen
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card outlined>
            <v-card-text class="text-center py-2 px-0">
              <div class="text-h6 font-weight-bold green--text">
                <v-icon small class="mr-1" color="green">mdi-check</v-icon
                >{{ statusCounts.approved }}
              </div>
              <div class="text-caption grey--text hidden-sm-only">
                Genehmigt
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card outlined>
            <v-card-text class="text-center py-2 px-0">
              <div class="text-h6 font-weight-bold red--text">
                <v-icon small class="mr-1" color="red">mdi-close</v-icon>
                {{ statusCounts.rejected }}
              </div>
              <div class="text-caption grey--text hidden-sm-only">
                Abgelehnt
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card outlined>
            <v-card-text class="text-center py-2 px-0">
              <div class="text-h6 font-weight-bold orange--text">
                <v-icon small class="mr-1" color="orange">mdi-alert</v-icon>
                {{ statusCounts.erroneous }}
              </div>
              <div class="text-caption grey--text hidden-sm-only">
                Fehlerhaft
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { db } from "@/firebase";

export default {
  name: "time-sheet-status-card",
  props: {
    timeSheets: {
      type: Array,
      default: () => [],
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      batchesLoading: false,
      selectedBatchSelector: { text: "Auswählen...", value: null, batch: null },
      payrollBatches: [],
    };
  },
  computed: {
    organizationId() {
      return this.$route.params.organizationId;
    },
    selectedBatch() {
      return this.selectedBatchSelector?.batch || null;
    },
    filteredTimeSheets() {
      if (!this.selectedBatch) {
        return this.timeSheets;
      }

      const startDate = this.selectedBatch.period.start.timestamp.toDate();
      const endDate = this.selectedBatch.period.end.timestamp.toDate();

      return this.timeSheets.filter((ts) => {
        if (!ts.billableDuration?.start?.timestamp) return false;
        const tsDate = ts.billableDuration.start.timestamp.toDate();
        return tsDate >= startDate && tsDate <= endDate;
      });
    },
    statusCounts() {
      const counts = {
        toReview: 0,
        approved: 0,
        rejected: 0,
        erroneous: 0,
      };

      this.filteredTimeSheets.forEach((ts) => {
        switch (ts.status) {
          case "toBeReviewed":
            counts.toReview++;
            break;
          case "approved":
            counts.approved++;
            break;
          case "rejected":
            counts.rejected++;
            break;
          case "erroneous":
          case "error":
            counts.erroneous++;
            break;
        }
      });

      return counts;
    },
  },
  watch: {
    timeSheets: {
      handler() {
        // Recalculate when time sheets change
        this.$forceUpdate();
      },
      deep: true,
    },
  },
  created() {
    if (this.autoLoad) {
      this.initialize();
    }
  },
  methods: {
    async initialize() {
      await this.loadPayrollBatches();
    },
    async loadPayrollBatches() {
      if (!this.organizationId) return;

      this.batchesLoading = true;
      try {
        const snapshot = await db
          .collection("organizations")
          .doc(this.organizationId)
          .collection("payrollBatches")
          .where("status", "in", [
            "warningsToBeClosed",
            "payslipsToBeGenerated",
          ])
          .limit(12) // Get last 12 batches
          .get();

        this.payrollBatches = [];
        snapshot.forEach((doc) => {
          this.payrollBatches.push(doc.data());
        });

        // Select last (most recent) batch by default
        if (
          this.payrollBatches.length > 0 &&
          !this.selectedBatchSelector.value
        ) {
          const firstBatch = this.payrollBatches[0];
          this.selectedBatchSelector = {
            text: firstBatch.title || this.formatBatchPeriod(firstBatch),
            value: firstBatch.meta.id,
            batch: firstBatch,
          };
        }
      } catch (error) {
        console.error("Error loading payroll batches:", error);
        this.$emit("error", error);
      } finally {
        this.batchesLoading = false;
      }
    },
    refreshData() {
      this.loading = true;
      this.$emit("refresh");
      // Simulate loading
      setTimeout(() => {
        this.loading = false;
      }, 500);
    },
    formatBatchPeriod(batch) {
      if (!batch.period?.start?.timestamp || !batch.period?.end?.timestamp) {
        return "Unbekannte Periode";
      }
      const start = this.formatDate(batch.period.start.timestamp);
      const end = this.formatDate(batch.period.end.timestamp);
      return `${start} - ${end}`;
    },
    formatDate(timestamp) {
      if (!timestamp) return "";
      const date = timestamp.toDate();
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.payroll-batch-select {
  max-width: 200px;
  font-size: 0.875rem;
}

.payroll-batch-select >>> .v-select__selection {
  font-size: 0.75rem;
}

.v-card__subtitle {
  padding: 12px 16px;
}
</style>
